// `localStorage` and not a cookie: the API is on another origin in production, and a third-party cookie is dropped by Safari.
const ACCESS = 'hp.access';
const REFRESH = 'hp.refresh';

let accessToken: string | null = null;

const listeners = new Set<() => void>();
const signOutListeners = new Set<() => void>();

const announce = () => {
  for (const listener of listeners) listener();
};

const claims = (token: string | null): Record<string, unknown> | null => {
  const payload = token?.split('.')[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

// Another tab rotated or dropped the pair: the cached copy here would send a token that is already dead.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== null && event.key !== ACCESS && event.key !== REFRESH) return;
    accessToken = null;
    announce();
    if (event.key !== ACCESS && localStorage.getItem(REFRESH) === null) {
      for (const listener of signOutListeners) listener();
    }
  });
}

export const tokenStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  onSignedOut(listener: () => void): () => void {
    signOutListeners.add(listener);
    return () => {
      signOutListeners.delete(listener);
    };
  },

  access(): string | null {
    if (accessToken === null) accessToken = localStorage.getItem(ACCESS);
    return accessToken;
  },

  reload(): void {
    accessToken = null;
  },

  fresh(token: string | null, marginMs = 5_000): boolean {
    const exp = claims(token)?.exp;
    return typeof exp === 'number' && exp * 1000 - marginMs > Date.now();
  },

  /** The signed-in user's id, read from the access token; for keys that belong to one reader. */
  subject(): string | null {
    const sub = claims(this.access())?.sub;
    return typeof sub === 'string' ? sub : null;
  },

  refresh(): string | null {
    return localStorage.getItem(REFRESH);
  },

  set(access: string, refresh: string): void {
    accessToken = access;
    localStorage.setItem(ACCESS, access);
    localStorage.setItem(REFRESH, refresh);
    announce();
  },

  clear(): void {
    accessToken = null;
    localStorage.removeItem(ACCESS);
    localStorage.removeItem(REFRESH);
    announce();
  },

  expire(): void {
    const hadSession = this.refresh() !== null || this.access() !== null;
    this.clear();
    if (!hadSession) return;
    for (const listener of signOutListeners) listener();
  },
};
